import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, vModelText as _vModelText, withKeys as _withKeys, createElementVNode as _createElementVNode, withDirectives as _withDirectives, normalizeStyle as _normalizeStyle, createVNode as _createVNode, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, resolveDirective as _resolveDirective } from "vue"

const _hoisted_1 = { class: "cx-input-content" }
const _hoisted_2 = ["disabled"]
const _hoisted_3 = ["disabled", "placeholder", "readonly", "step"]
const _hoisted_4 = {
  key: 0,
  class: "cx-input-appendIcon-container"
}
const _hoisted_5 = {
  key: 0,
  class: "cx-input-step"
}
const _hoisted_6 = {
  key: 1,
  class: "cx-input-validation"
}

import { computed, onUnmounted, ref, reactive, watch, Ref, WritableComputedRef } from 'vue';
import { useVuelidate, ValidationArgs } from '@vuelidate/core';
import CxIcon from '@/components/CxIcon.vue';
import CxButton from '@/components/CxButton.vue';
import CxLabel from '@/components/CxLabel.vue';
import CxSpinner from '@/components/CxSpinner.vue';
import { isNil, floor, ceil, isNumber, round } from 'lodash';

enum StepOperations {
  add = 'add',
  sub = 'sub'
}

type SupportedInputTypes = 'text' | 'number';

interface Props {
  modelValue?: string | number,
  validationRules?: ValidationArgs<unknown> | Ref<ValidationArgs<unknown>>
  type?: SupportedInputTypes
  disabled?: boolean
  placeholder?: string
  label?: string
  showStep?: boolean
  step?: number
  precision?: number
  readonly?: boolean
  icon?: string
  iconSize?: number
  loading?: boolean,
  tooltip?: string,
  errorPosition?: string,
  appendIcon?: string | null,
  appendIconSize?: number,
  showErrors?: boolean,
  immediatelyShowErrors?: boolean
}


export default /*#__PURE__*/_defineComponent({
  __name: 'CxInput',
  props: {
    modelValue: {},
    validationRules: {},
    type: { default: 'text' },
    disabled: { type: Boolean },
    placeholder: {},
    label: {},
    showStep: { type: Boolean, default: true },
    step: { default: 1 },
    precision: { default: 2 },
    readonly: { type: Boolean },
    icon: {},
    iconSize: { default: 9 },
    loading: { type: Boolean },
    tooltip: {},
    errorPosition: { default: 'bottom' },
    appendIcon: { default: null },
    appendIconSize: { default: 9 },
    showErrors: { type: Boolean, default: true },
    immediatelyShowErrors: { type: Boolean, default: false }
  },
  emits: ["update:modelValue"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

// State for validation
const state = reactive({
  modelValue: props.modelValue
});

const initalizeVuelidate = () => {
  return props.validationRules !== undefined ? useVuelidate(props.validationRules, state) : useVuelidate();
};

let v$ = initalizeVuelidate();

const emit = __emit;

const isInputValid = ref(true);
const errorMessage = ref('');

const innerValue: WritableComputedRef<string | number | null> = computed({
  get(): string {
    let displayVal = props.modelValue ? `${props.modelValue}` : '';
    if (isNumber(props.modelValue) && !Number.isNaN(props.modelValue) && !isNil(props.precision)) displayVal = `${round(props.modelValue, props.precision)}`;
    return displayVal;
  },
  set(newValue: string): void {
    state.modelValue = newValue;
    if (props.immediatelyShowErrors && v$.value.$errors.length) {
      isInputValid.value = false;
      errorMessage.value = v$.value.$errors[0].$message.toString();
    } else if (props.immediatelyShowErrors && v$.value.$errors.length === 0) {
      isInputValid.value = true;
      errorMessage.value = '';
    }
    if (!props.readonly) {
      emit('update:modelValue', newValue);
    }
  },
});

const onBlur = () => {
  if (!props.immediatelyShowErrors && v$.value.$errors.length) {
    isInputValid.value = false;
    errorMessage.value = v$.value.$errors[0].$message.toString();
  } else if (!props.immediatelyShowErrors && v$.value.$errors.length === 0) {
    isInputValid.value = true;
    errorMessage.value = '';
  }
};

let innerStepInterval: ReturnType<typeof setInterval> = 0;
const isTooltipActive = ref(false);
const hideOnLoading = computed(() => {
  return props.loading ? 'visibility: hidden;' : 'visibility: visible;';
});

const getValueRest = (stringValue: string, stepPrecision: number) => {
  if (isNil(stringValue)) {
    return '';
  }
  const splittedDigits = stringValue.toString().split('.');
  const digits = splittedDigits.length > 1 ? splittedDigits[1] : null;

  if (!digits) {
    return '';
  }
  const restOfNumber = digits.slice(stepPrecision);

  return stepPrecision === 0 ? `.${restOfNumber}` : restOfNumber;
};

const applyStep = (operation: StepOperations) => {
  const operationModel: Record<StepOperations, (base: number, step: number) => number> = {
    [StepOperations.add](base, step) {
      return base + step;
    },
    [StepOperations.sub](base, step) {
      return base - step;
    }
  };

  let number = state.modelValue ? state.modelValue as number : 0;
  if (Number.isNaN(number)) {
    number = 0;
  }
  const splittedStep = (`${props.step}`).split('.');
  const stepPrecision = splittedStep.length > 1 ? splittedStep[1].length : 0;
  const base = number > 0 ? floor(number, stepPrecision) : ceil(number, stepPrecision);
  const result = operationModel[operation](base, props.step);
  state.modelValue = result.toFixed(stepPrecision) + getValueRest(state.modelValue as string, stepPrecision);
  emit('update:modelValue', state.modelValue);
  if (v$.value.$errors.length) {
    isInputValid.value = false;
    errorMessage.value = v$.value.$errors[0].$message.toString();
  } else {
    isInputValid.value = true;
    errorMessage.value = '';
  }
};

const startApplyStep = (operation: StepOperations) => {
  if (!innerStepInterval) {
    applyStep(operation);
    innerStepInterval = setInterval(() => applyStep(operation), 150);
  }
};

const stopApplyStep = () => {
  if (innerStepInterval) {
    clearInterval(innerStepInterval);
    innerStepInterval = 0;
  }
};

watch(() => (props.validationRules), () => {
  v$ = initalizeVuelidate();
  v$.value.$validate();
  if (v$.value.$errors.length) {
    isInputValid.value = false;
    errorMessage.value = v$.value.$errors[0].$message.toString();
  } else {
    isInputValid.value = true;
    errorMessage.value = '';
  }
});

watch(() => (props.modelValue), (newValue) => {
  state.modelValue = newValue;
});

onUnmounted(() => {
  stopApplyStep();
});


return (_ctx: any,_cache: any) => {
  const _directive_cx_tooltip = _resolveDirective("cx-tooltip")!

  return (_openBlock(), _createElementBlock("div", {
    class: "cx-input",
    onMouseenter: _cache[5] || (_cache[5] = ($event: any) => (isTooltipActive.value=true)),
    onMouseleave: _cache[6] || (_cache[6] = ($event: any) => (isTooltipActive.value=false))
  }, [
    (_ctx.label)
      ? (_openBlock(), _createBlock(CxLabel, {
          key: 0,
          label: _ctx.label
        }, null, 8, ["label"]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_1, [
      _withDirectives((_openBlock(), _createElementBlock("div", {
        class: _normalizeClass(`cx-position-${_ctx.errorPosition}`)
      }, [
        _createElementVNode("div", {
          class: _normalizeClass(["cx-input-container", { 'cx-input-container--error': _unref(v$).modelValue && !isInputValid.value }]),
          disabled: _ctx.disabled || null
        }, [
          _createElementVNode("div", {
            class: "cx-input-wrapper",
            style: _normalizeStyle(hideOnLoading.value)
          }, [
            _withDirectives(_createElementVNode("input", {
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((innerValue).value = $event)),
              class: "cx-input__input",
              disabled: _ctx.disabled,
              placeholder: _ctx.placeholder,
              readonly: _ctx.readonly,
              step: _ctx.step,
              onKeydown: [
                _cache[1] || (_cache[1] = _withKeys(($event: any) => (startApplyStep(StepOperations.add)), ["up"])),
                _cache[2] || (_cache[2] = _withKeys(($event: any) => (startApplyStep(StepOperations.sub)), ["down"]))
              ],
              onKeyup: [
                _withKeys(stopApplyStep, ["up"]),
                _withKeys(stopApplyStep, ["down"])
              ],
              onBlur: onBlur
            }, null, 40, _hoisted_3), [
              [_vModelText, innerValue.value]
            ])
          ], 4),
          (_ctx.appendIcon || _ctx.showStep)
            ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                (!_ctx.disabled && _ctx.showStep && _ctx.type === 'number')
                  ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                      _createVNode(CxButton, {
                        iconSize: "12",
                        flat: "",
                        class: "cx-input-step-add",
                        icon: "keyboard_arrow_up",
                        onMousedown: _cache[3] || (_cache[3] = () => startApplyStep(StepOperations.add)),
                        onMouseup: stopApplyStep,
                        onMouseout: stopApplyStep
                      }),
                      _createVNode(CxButton, {
                        iconSize: "12",
                        flat: "",
                        class: "cx-input-step-sub",
                        icon: "keyboard_arrow_down",
                        onMousedown: _cache[4] || (_cache[4] = () => startApplyStep(StepOperations.sub)),
                        onMouseup: stopApplyStep,
                        onMouseout: stopApplyStep
                      })
                    ]))
                  : _createCommentVNode("", true),
                (_ctx.appendIcon && _ctx.type !== 'number')
                  ? (_openBlock(), _createBlock(CxIcon, {
                      key: 1,
                      class: "cx-input-icon",
                      icon: _ctx.appendIcon,
                      size: _ctx.appendIconSize
                    }, null, 8, ["icon", "size"]))
                  : _createCommentVNode("", true)
              ]))
            : _createCommentVNode("", true)
        ], 10, _hoisted_2),
        (_ctx.loading)
          ? (_openBlock(), _createBlock(CxSpinner, {
              key: 0,
              size: 15,
              width: 1
            }))
          : _createCommentVNode("", true),
        (!isInputValid.value && _ctx.showErrors && !_ctx.loading)
          ? (_openBlock(), _createElementBlock("span", _hoisted_6, _toDisplayString(errorMessage.value), 1))
          : _createCommentVNode("", true)
      ], 2)), [
        [_directive_cx_tooltip, {
          text: _ctx.tooltip,
          show: isTooltipActive.value && _ctx.tooltip && !_ctx.loading && !_ctx.disabled,
          position: 'top-left',
          offsetTop: -5,
          maxWidth: 400,
        }]
      ])
    ])
  ], 32))
}
}

})